import apiClient from './client';
import { API_ENDPOINTS } from './config';
import type {
	NetworkAreas,
	RotationListing,
	RotationListingRequest,
	RotationStaff,
	StaffEngagement,
	StaffEngagements,
	StaffInvite,
	StaffInviteRequest,
	StaffInviteStatus,
} from '../types/staffNetwork';

/**
 * Rotation staff across salons: the provider's directory search and invitations,
 * and the staff member's own listing, invitations and engagements.
 */
export const staffNetworkApi = {
	// ─── Provider ───────────────────────────────────────────────

	areas: async (): Promise<NetworkAreas> =>
		apiClient.get<NetworkAreas>(API_ENDPOINTS.STAFF_NETWORK.AREAS),

	search: async (params: { state?: string; city?: string; query?: string }): Promise<RotationStaff[]> => {
		const qs = new URLSearchParams();
		if (params.state) qs.append('state', params.state);
		if (params.city) qs.append('city', params.city);
		if (params.query) qs.append('query', params.query);
		const suffix = qs.toString();
		return apiClient.get<RotationStaff[]>(
			`${API_ENDPOINTS.STAFF_NETWORK.SEARCH}${suffix ? `?${suffix}` : ''}`
		);
	},

	sendInvite: async (locationId: number, data: StaffInviteRequest): Promise<StaffInvite> =>
		apiClient.post<StaffInvite>(
			`${API_ENDPOINTS.STAFF_NETWORK.INVITES}?locationId=${locationId}`,
			data
		),

	sentInvites: async (status?: StaffInviteStatus): Promise<StaffInvite[]> =>
		apiClient.get<StaffInvite[]>(
			`${API_ENDPOINTS.STAFF_NETWORK.INVITES}${status ? `?status=${status}` : ''}`
		),

	withdrawInvite: async (id: number): Promise<StaffInvite> =>
		apiClient.post<StaffInvite>(API_ENDPOINTS.STAFF_NETWORK.WITHDRAW_INVITE(id)),

	engagementsOf: async (staffId: number, locationId: number, date?: string): Promise<StaffEngagements> =>
		apiClient.get<StaffEngagements>(
			`${API_ENDPOINTS.STAFF_NETWORK.ENGAGEMENTS_OF(staffId)}?locationId=${locationId}${
				date ? `&date=${date}` : ''
			}`
		),

	// ─── Staff ──────────────────────────────────────────────────

	myInvites: async (includeDecided = false): Promise<StaffInvite[]> =>
		apiClient.get<StaffInvite[]>(
			`${API_ENDPOINTS.STAFF_NETWORK.MY_INVITES}${includeDecided ? '?includeDecided=true' : ''}`
		),

	acceptInvite: async (id: number): Promise<StaffEngagement> =>
		apiClient.post<StaffEngagement>(API_ENDPOINTS.STAFF_NETWORK.ACCEPT_INVITE(id)),

	declineInvite: async (id: number): Promise<StaffInvite> =>
		apiClient.post<StaffInvite>(API_ENDPOINTS.STAFF_NETWORK.DECLINE_INVITE(id)),

	myEngagements: async (): Promise<StaffEngagement[]> =>
		apiClient.get<StaffEngagement[]>(API_ENDPOINTS.STAFF_NETWORK.MY_ENGAGEMENTS),

	myListing: async (): Promise<RotationListing> =>
		apiClient.get<RotationListing>(API_ENDPOINTS.STAFF_NETWORK.MY_LISTING),

	updateMyListing: async (data: RotationListingRequest): Promise<RotationListing> =>
		apiClient.put<RotationListing>(API_ENDPOINTS.STAFF_NETWORK.MY_LISTING, data),
};

export default staffNetworkApi;
